import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { duelRepository } from "@/config/dataSource";
import type { CreateDuelInput } from "@/modules/leagues/types/duel";

const keys = {
  myDuels: (leagueId: string) => ["duels", leagueId] as const,
};

export function useMyDuels(leagueId: string) {
  return useQuery({
    queryKey: keys.myDuels(leagueId),
    queryFn: () => duelRepository.getMyDuels(leagueId),
  });
}

export function useCreateDuel(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDuelInput) => duelRepository.createDuel(leagueId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.myDuels(leagueId) }),
  });
}

export function useAcceptDuel(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ duelId, palpite }: { duelId: string; palpite: { homeScore: number; awayScore: number } }) =>
      duelRepository.acceptDuel(leagueId, duelId, palpite),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.myDuels(leagueId) }),
  });
}

export function useDeclineDuel(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (duelId: string) => duelRepository.declineDuel(leagueId, duelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.myDuels(leagueId) }),
  });
}

export function useCancelDuel(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (duelId: string) => duelRepository.cancelDuel(leagueId, duelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.myDuels(leagueId) }),
  });
}

export function useDeleteDuel(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (duelId: string) => duelRepository.deleteDuel(leagueId, duelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.myDuels(leagueId) }),
  });
}
